import { useEffect, useMemo, useState } from "react";
import qapi from "views_shared/app/javascript/api";
import qlog from "views_shared/app/javascript/scripts/qlog";
import { mediaOutletUrl } from "views_shared/app/javascript/utils/urls";

// Builds the public, always-visible base row list: one row per outlet, deduped from the profile's
// media jobs (a person can have >1 job at the same outlet across time). Order follows mediaJobs'
// own invariant (most-recent-started-first), keeping the first (most recent) job's outlet fields
// whenever an outlet repeats.
function dedupeOutletsFromJobs(mediaJobs) {
  const seenOutletIds = new Set();
  const outlets = [];

  mediaJobs.forEach((job) => {
    if (seenOutletIds.has(job.mediaOutletId)) return;
    seenOutletIds.add(job.mediaOutletId);
    outlets.push({
      id: job.mediaOutletId,
      href: mediaOutletUrl({ slug: job.mediaOutletSlug, migrate_id: job.mediaOutletMigrateId }),
      imageUrl: job.mediaOutletProfileImageUrl,
      title: job.mediaOutletName,
    });
  });

  return outlets;
}

/**
 * useMediaOutletsRail — data half of the media profile's "Media Outlets" rail card (the chrome
 * half is media_outlets_rail_card). A UNION of two sources: the base row list is the profile's
 * own deduped employment history (`mediaJobs`, already fetched by the page for the Employment
 * section) — public, and shown to every viewer regardless of entitlement — followed by the
 * outlets this reporter's requests name, read through the get_media_outlets_for_media_profile
 * RPC, which stays entitlement-gated server-side (unentitled viewers get an empty array back,
 * not an error, and therefore see only the employment rows).
 *
 * Employment rows come first, in employment order; a matched RPC row overlays its `requestCount`
 * onto them (an unmatched employment row simply carries no count — never "0"). RPC rows whose
 * outlet ISN'T in the employment set are APPENDED, in the RPC's own most-requests-first order —
 * a reporter with no employment rows still gets a rail of the outlets they post for, which is
 * key reporter-profile content, not a leak: the association is the same paid media information
 * the requests view already gates.
 *
 * The fetched rows are tagged with the profile id they were fetched FOR, and only used when that
 * id still matches the id currently being rendered. On a same-instance profile switch (this hook
 * re-used for a new `mediaProfileId` rather than remounted), the rows read for the OLD profile
 * would otherwise still be sitting in state for the brief window before the effect re-fires and
 * its promise resolves — long enough to transiently pair the new profile's employment list with
 * the previous profile's stale rows. Tagging and gating the merge (and deriving `isLoading`) on
 * that identity match closes the window instead of racing it.
 *
 * Never throws: a decorative rail card must not take the profile page down, so both failure
 * shapes (`createResource` resolving `{ error }` on a non-2xx, and a rejected transport promise)
 * log and settle with no RPC rows merged (the employment list still renders).
 *
 * @param {Object} options
 * @param {number|string} [options.mediaProfileId] - The media profile whose outlet rail to load
 * @param {Array} [options.mediaJobs] - The profile's already-loaded media jobs (deserialized,
 *   camelCase — see publications_modal_body/serialization.js), used to build the base outlet list
 * @returns {{ outlets: Array, isLoading: boolean }} `outlets` are row items (`{ id, href,
 *   imageUrl, title, requestCount }`): deduped employment outlets first (employment order), then
 *   request-derived outlets not already listed (most requests first); `requestCount` is present
 *   only where the RPC returned a row for the CURRENT profile
 */
function useMediaOutletsRail({ mediaProfileId, mediaJobs = [] }) {
  const normalizedProfileId = mediaProfileId ?? null;
  // `fetchedForId` names the profile the resolved (or settled-empty) `rows` belong to, so a
  // caller can never merge rows fetched for a different profile than the one it's rendering.
  const [rpcResult, setRpcResult] = useState({ fetchedForId: null, rows: [] });

  useEffect(() => {
    // No profile id yet (still loading): settle empty rather than leaving the card in a
    // skeleton that never resolves.
    if (!mediaProfileId) {
      setRpcResult({ fetchedForId: null, rows: [] });
      return undefined;
    }

    let componentIsMounted = true;

    const load = async () => {
      try {
        const response = await qapi.requests.getMediaOutletsForMediaProfile(mediaProfileId);
        if (!componentIsMounted) return;

        // createResource RESOLVES `{ error }` on a non-2xx instead of throwing, so THIS branch —
        // not the catch below — is the one an ordinary PostgREST failure takes.
        if (response?.error) {
          qlog.error("Media Outlets rail lookup failed:", response.error);
          setRpcResult({ fetchedForId: mediaProfileId, rows: [] });
          return;
        }

        const rows = Array.isArray(response?.data) ? response.data : [];
        setRpcResult({ fetchedForId: mediaProfileId, rows });
      } catch (error) {
        // The transport itself failed (network down, token refresh threw) — a rejection, not `{ error }`.
        qlog.error("Media Outlets rail lookup failed:", error);
        if (componentIsMounted) setRpcResult({ fetchedForId: mediaProfileId, rows: [] });
      }
    };
    load();

    return () => {
      componentIsMounted = false;
    };
  }, [mediaProfileId]);

  // Loading (and therefore "don't trust the merge yet") is exactly "the rows we're holding
  // weren't fetched for the profile we're currently asked to render" — covers both the initial
  // fetch and a same-instance profile switch whose new read hasn't resolved yet.
  const isLoading = rpcResult.fetchedForId !== normalizedProfileId;

  const outlets = useMemo(() => {
    const rpcRows = isLoading ? [] : rpcResult.rows;
    const counts = {};
    rpcRows.forEach((row) => {
      counts[row.media_outlet_id] = row.request_count;
    });

    const employmentOutlets = dedupeOutletsFromJobs(mediaJobs).map((outlet) => ({
      ...outlet,
      requestCount: counts[outlet.id],
    }));

    const employmentOutletIds = new Set(employmentOutlets.map((outlet) => outlet.id));
    const requestDerivedOutlets = rpcRows
      .filter((row) => !employmentOutletIds.has(row.media_outlet_id))
      .map((row) => ({
        id: row.media_outlet_id,
        href: mediaOutletUrl({ slug: row.media_outlet_slug, migrate_id: row.media_outlet_migrate_id }),
        imageUrl: row.media_outlet_profile_image_url,
        title: row.media_outlet_name,
        requestCount: row.request_count,
      }));

    return [...employmentOutlets, ...requestDerivedOutlets];
  }, [mediaJobs, rpcResult, isLoading]);

  return { outlets, isLoading };
}

export default useMediaOutletsRail;
